<template>
  <header class="nav" :class="{ scrolled }">
    <div class="container nav-inner">
      <router-link to="/" class="brand">
        <span class="brand-mark">静</span>
        <span class="brand-text">
          <span class="brand-name">余静</span>
          <span class="brand-sub">kotlinx</span>
        </span>
      </router-link>

      <nav class="nav-links" :class="{ open: menuOpen }">
        <router-link
          v-for="link in navLinks"
          :key="link.key"
          :to="link.to"
          class="nav-link"
          @click="menuOpen = false"
        >
          <i v-if="link.icon" :class="link.icon" class="nav-link-icon"></i>
          <span>{{ link.label }}</span>
        </router-link>
        <a :href="site.github" target="_blank" rel="noopener" class="nav-link nav-github" title="GitHub">
          <i class="fa-brands fa-github"></i>
        </a>
      </nav>

      <button class="menu-toggle" :class="{ open: menuOpen }" @click="menuOpen = !menuOpen" aria-label="菜单">
        <span></span><span></span><span></span>
      </button>
    </div>
  </header>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { navLinks, site } from '@/data/site.js'

const scrolled = ref(false)
const menuOpen = ref(false)

const onScroll = () => { scrolled.value = window.scrollY > 20 }

onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true })
  onScroll()
})
onUnmounted(() => window.removeEventListener('scroll', onScroll))
</script>

<style scoped>
.nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 72px;
  z-index: 100;
  background: rgba(10, 11, 14, 0.6);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  border-bottom: 1px solid transparent;
  transition: all 0.3s ease;
}
.nav.scrolled {
  background: rgba(10, 11, 14, 0.85);
  border-bottom-color: var(--border-soft);
}
.nav-inner {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.brand {
  display: flex;
  align-items: center;
  gap: 12px;
}
.brand-mark {
  width: 38px;
  height: 38px;
  display: grid;
  place-items: center;
  background: var(--accent);
  color: var(--bg-base);
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 1.15rem;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(56, 189, 248, 0.35);
  transition: all 0.3s ease;
}
.brand:hover .brand-mark {
  transform: rotate(-6deg) scale(1.05);
  box-shadow: 0 6px 24px rgba(56, 189, 248, 0.5);
}
.brand-text {
  display: flex;
  flex-direction: column;
  line-height: 1.1;
}
.brand-name {
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 1.05rem;
}
.brand-sub {
  font-family: var(--font-mono);
  font-size: 0.72rem;
  color: var(--text-muted);
  letter-spacing: 0.05em;
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 4px;
}
.nav-link {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 14px;
  font-size: 0.92rem;
  color: var(--text-muted);
  border-radius: 8px;
  position: relative;
  transition: all 0.2s ease;
}
.nav-link-icon {
  font-size: 0.85rem;
  opacity: 0.7;
  transition: all 0.2s ease;
}
.nav-link:hover .nav-link-icon {
  opacity: 1;
  transform: translateY(-1px);
}
.nav-link:hover {
  color: var(--text);
  background: var(--bg-elevated);
}
.nav-link.router-link-active {
  color: var(--accent);
}
.nav-link.router-link-active::after {
  content: '';
  position: absolute;
  left: 14px;
  right: 14px;
  bottom: 2px;
  height: 2px;
  background: var(--accent);
  border-radius: 2px;
}
.nav-github {
  font-size: 1.1rem;
  margin-left: 6px;
}

.menu-toggle {
  display: none;
  flex-direction: column;
  gap: 5px;
  padding: 8px;
}
.menu-toggle span {
  width: 22px;
  height: 2px;
  background: var(--text);
  border-radius: 2px;
  transition: all 0.3s ease;
}
.menu-toggle.open span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
.menu-toggle.open span:nth-child(2) { opacity: 0; }
.menu-toggle.open span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

@media (max-width: 860px) {
  .menu-toggle { display: flex; }
  .nav-links {
    position: fixed;
    top: 72px;
    left: 0;
    right: 0;
    flex-direction: column;
    align-items: stretch;
    background: var(--bg-surface);
    border-bottom: 1px solid var(--border);
    padding: 12px 20px 20px;
    gap: 4px;
    transform: translateY(-120%);
    opacity: 0;
    pointer-events: none;
    transition: all 0.3s ease;
  }
  .nav-links.open {
    transform: translateY(0);
    opacity: 1;
    pointer-events: auto;
  }
  .nav-link { padding: 12px 14px; }
  .nav-github { margin-left: 0; }
}
</style>
